<template>
  <Modal
    size="large"
    v-slot="{close}"
    title="Image par IA"
    close-id="generation-modal-close"
    :no-padding="true"
  >
    <div class="px-6 py-4 max-h-[80vh] overflow-y-auto">

      <span class="block pb-3 text-lg font-medium">Paramètres</span>
      <div class="grid gap-6 grid-cols-4">
        <IAModeSelector :image="config.frame.find(f => f.key === frame).image" title="Cadre" :selected="config.frame.find(f => f.key === frame).name">
          <template #content>
            <div class="grid-cols-3  w-[450px]  grid gap-4">
              <ImageSelector
                  v-for="f in Object.values(config.frame)"
                  :selected="frame === f.key"
                  @click="frame = f.key"
                  :image="f.image"
                  :title="f.name"/>
            </div>
          </template>
        </IAModeSelector>

        <IAModeSelector :image="config.angles.find(f => f.key === angle).image" title="Angle" :selected="config.angles.find(f => f.key === angle).name">
          <template #content>
            <div class="grid-cols-4 w-[600px]  grid gap-4">
              <ImageSelector
                  v-for="f in Object.values(config.angles)"
                  :selected="angle === f.key"
                  @click="angle = f.key"
                  :image="f.image"
                  :title="f.name"/>
            </div>
          </template>
        </IAModeSelector>

        <IAModeSelector :image="config.styles.find(f => f.key === style).image"  title="Style" :selected="config.styles.find(f => f.key === style).name">
          <template #content>
            <div class="grid-cols-6 w-[1000px] grid gap-4">
              <ImageSelector
                  v-for="f in Object.values(config.styles)"
                  :selected="style === f.key"
                  @click="style = f.key"
                  :image="f.image"
                  :title="f.name"/>
            </div>
          </template>
        </IAModeSelector>

        <IAModeSelector :image="colorImage" title="Couleur" v-if="hasColor" :selected="config.colors.find(f => f.key === color).name">
          <template #content>
            <div class="grid-cols-7 w-[600px] grid gap-4">
              <ImageSelector
                  v-for="f in Object.values(config.colors)"
                  :selected="color === f.key"
                  @click="color = f.key"
                  :image="getColorImage(f.key)"
                  :title="f.name"/>
            </div>
          </template>
        </IAModeSelector>

        <IAModeSelector :image="config.tableTexture.find(f => f.key === tabletop).image" title="Table" v-if="hasTableTop" :selected="config.tableTexture.find(f => f.key === tabletop).name">
          <template #content>
            <div class="grid-cols-5 w-[600px] grid gap-4">
              <ImageSelector
                  v-for="f in Object.values(config.tableTexture)"
                  :selected="tabletop === f.key"
                  @click="tabletop = f.key"
                  :image="f.image"
                  :title="f.name"/>
            </div>
          </template>
        </IAModeSelector>
      </div>

      <span class="block pb-3 text-lg font-medium mt-5">Produits concernés ({{localProductIds.length}})</span>

      <div class="grid grid-cols-4 gap-4" v-if="concernedProducts.length > 0">
        <IaProductSelected
          v-for="product in concernedProducts"
          :key="product.id"
          :product="product"
          :loading="isProductLoading(product.id)"
          :override-image="getGeneratedImage(product.id)"
          @remove-product="handleRemoveProduct"
          @regenerate-product="handleRegenerateProduct"
          @useImage="handleUseImage(product.id)"
        />
      </div>

      <div v-else class="text-center py-8 text-gray-500">
        Aucun produit sélectionné
      </div>

      <div class="mt-6 flex gap-4 items-center">
        <!-- Initial generation button -->
        <button class="btn bg-blue-600 text-white" v-if="generationResults.length === 0" @click="generateAllImages">
          Générer les images
        </button>

        <!-- Loading state -->
        <button class="btn bg-blue-600 text-white" v-if="generationResults.length > 0 && generationResults.some(e => e.status === 'pending')" disabled>
          Génération en cours...
        </button>

        <!-- When generation is complete and no parameters changed -->
        <button class="btn bg-blue-600 text-white" v-if="generationResults.length > 0 && generationResults.every(e => e.status === 'completed') && !parametersChanged" @click="useAllImages">
          Utiliser toutes les images
        </button>

        <!-- When generation is complete and parameters have changed -->
        <template v-if="generationResults.length > 0 && generationResults.every(e => e.status === 'completed') && parametersChanged">
          <button class="btn bg-blue-600 text-white" @click="useAllImages">
            Utiliser toutes les images
          </button>
          <button class="btn bg-gray-500 text-white" @click="regenerateAllImages">
            Regénérer toutes les images
          </button>
        </template>

        <span class="text-left text-neutral-600" v-if="generationResults.length > 0 && generationResults.some(e => e.status === 'pending')">
          Cette opération peut prendre 1-2 minutes, ne pas fermer la page.
        </span>
      </div>

    </div>
  </Modal>
</template>

<script setup>
import Modal from '@/components/Ui/Modals/Modal.vue'
import IAModeSelector from "@/components/Menu/Elements/IAModeSelector.vue";
import ImageSelector from "@/components/Menu/Elements/ImageSelector.vue";
import IaProductSelected from "@/components/Menu/Elements/IaProductSelected.vue";
import {computed, ref, onUnmounted} from "vue";
import {config, sendImageGenerationRequest, checkGenerationStatus} from '@/services/ImageGenerationService.js'
import {useStore} from 'vuex'
import {useVfm} from "vue-final-modal";
import EventBus from "@/EventBus";


const store = useStore()

const frame = ref('studio');
const angle = ref('upper-front');
const style = ref('realist');
const color = ref('black')
const tabletop = ref('darkwood')

// Store original parameters when generation is done
const originalParams = ref(null)

const props = defineProps({
  productIds: {
    type: Array,
    default: () => []
  }
})

const localProductIds = ref([...props.productIds])

const step = ref(0);
const generationResults = ref([]);
const statusCheckInterval = ref(null);
const pendingGenerations = ref(new Set()); // Track generations being checked


async function generateAllImages(){
  step.value = 1;
  const results = await sendImageGenerationRequest(
      store.getters['currentMerchant'].id,
      localProductIds.value,
      config.angles.find(f => f.key === angle.value).prompt + ' framing: food should fill 60% of the photo, square photo.',
      putVariablesInPrompt(
          config.frame.find(f => f.key === frame.value).prompt + ' ' + config.styles.find(f => f.key === style.value).prompt,
          config.colors.find(f => f.key === color.value).name,
          config.tableTexture.find(f => f.key === tabletop.value).prompt,
      )
  );

  generationResults.value = results;

  // Add all generation IDs to pending list
  results.forEach(result => {
    pendingGenerations.value.add(result.generationId);
  });

  startStatusCheckingIfNeeded();
}

async function handleUseImage(productId){
  await useImages([productId]);
  localProductIds.value = localProductIds.value.filter(id => id !== productId)
  if(localProductIds.value.length === 0){
    close();
  }
}

function close(){
  console.log('close');
  useVfm().close('generation-modal-close');
}

async function useAllImages(){
  await useImages(concernedProducts.value.map(r => r.id));
  close();
}

async function regenerateAllImages(){
  step.value = 1;
  // Clear previous results for these products
  const productIdsToRegenerate = localProductIds.value;
  generationResults.value = generationResults.value.filter(r => !productIdsToRegenerate.includes(r.productId));

  const results = await sendImageGenerationRequest(
      store.getters['currentMerchant'].id,
      productIdsToRegenerate,
      config.angles.find(f => f.key === angle.value).prompt + ' framing: food should fill 60% of the photo, square photo.',
      putVariablesInPrompt(
          config.frame.find(f => f.key === frame.value).prompt + ' ' + config.styles.find(f => f.key === style.value).prompt,
          config.colors.find(f => f.key === color.value).name,
          config.tableTexture.find(f => f.key === tabletop.value).prompt,
      )
  );

  // Add new results
  generationResults.value.push(...results);

  // Add all generation IDs to pending list
  results.forEach(result => {
    pendingGenerations.value.add(result.generationId);
  });

  startStatusCheckingIfNeeded();
}

async function useImages(productIds){
  console.log('Using images for products:', productIds);
  const update = [];

  for(let productId of productIds){
    // get path from image_url
    console.log(getGeneratedImage(productId));
    const url = new URL(getGeneratedImage(productId));
    // remove first slash from pathname
    const path = url.pathname.startsWith('/') ? url.pathname.slice(1) : url.pathname;

    update.push({
      id: productId,
      imageData: [
        {
          size: "base",
          path: path,
          url: url.href
        },
        {
          size: "800x800",
          path: path,
          url: url.href
        },
        {
          size: "250x250",
          path: path,
          url: url.href
        }
      ]
    });

    EventBus.emit('menu:editor:image:'+ productId, {url: url.href})
  }


  await store.dispatch('menu/updateMenuItemBatchAction', {
    merchantId: store.getters['currentMerchant'].id,
    update: update,
    categoryForce: false
  })
}


async function checkPendingGenerations() {
  if (pendingGenerations.value.size === 0) {
    // No pending generations, stop checking
    if (statusCheckInterval.value) {
      clearInterval(statusCheckInterval.value);
      statusCheckInterval.value = null;
    }
    return;
  }

  const generationIds = Array.from(pendingGenerations.value);

  try {
    const statusResponse = await checkGenerationStatus(
      store.getters['currentMerchant'].id,
      generationIds
    );

    // Update generation results and remove completed ones from pending
    statusResponse.forEach(status => {
      // Update the generation result
      const existingIndex = generationResults.value.findIndex(r => r.generationId === status.generationId);
      if (existingIndex !== -1) {
        generationResults.value[existingIndex] = status;
      } else {
        generationResults.value.push(status);
      }

      // Remove from pending if completed
      if (status.status !== "pending" && status.image_url !== null) {
        pendingGenerations.value.delete(status.generationId);
      }
    });

    // Check if we should move to next step (all original generations completed)
    const allOriginalCompleted = generationResults.value
      .filter(r => localProductIds.value.includes(r.productId))
      .every(r => r.status !== "pending" && r.image_url !== null);

    if (allOriginalCompleted && step.value === 1) {
      step.value = 2;
      // Store current parameters as original when generation is complete
      originalParams.value = {
        frame: frame.value,
        angle: angle.value,
        style: style.value,
        color: color.value,
        tabletop: tabletop.value
      };
    }

  } catch (error) {
    console.error('Error checking generation status:', error);
  }
}


function putVariablesInPrompt(prompt, color, tabletop){
  return prompt
      .replace('%color%', color)
      .replace('%tabletop%', tabletop)
}

const concernedProducts = computed(() => {
  if (!store.getters['menu/menu'] || !localProductIds.value.length) {
    return []
  }
  return store.getters['menu/menu'].items.filter(item =>
    localProductIds.value.includes(item.id)
  )
})

const colorImage = computed(() => {
  return getColorImage(color.value);
})
function getColorImage(colorKey) {
  const colorConfig = config.colors.find(c => c.key === colorKey);
  return colorConfig ? `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='500' height='500' viewBox='0 0 500 500'><rect width='500' height='500' fill='%23${colorConfig.code.replace('#', '')}'/></svg>` : '';
}

const hasColor = computed(() => ['studio', 'color'].includes(frame.value))
const hasTableTop = computed(() => ['wood-brick'].includes(frame.value))

const parametersChanged = computed(() => {
  if (!originalParams.value) return false;

  return originalParams.value.frame !== frame.value ||
         originalParams.value.angle !== angle.value ||
         originalParams.value.style !== style.value ||
         originalParams.value.color !== color.value ||
         originalParams.value.tabletop !== tabletop.value;
})

const handleRemoveProduct = (productId) => {
  localProductIds.value = localProductIds.value.filter(id => id !== productId)
  if(localProductIds.value.length === 0){
    close();
  }
}

const isProductLoading = (productId) => {
  return generationResults.value.some(result =>
      result.productId === productId && result.status === "pending"
  );
}

const getGeneratedImage = (productId) => {
  const result = generationResults.value.find(result => result.productId === productId);
  return result && result.image_url ? result.image_url : '';
}

const handleRegenerateProduct = async (productId) => {
  try {
    const result = await sendImageGenerationRequest(
        store.getters['currentMerchant'].id,
        [productId],
        config.angles.find(f => f.key === angle.value).prompt + ' framing: food should fill 60% of the photo, square photo.',
        putVariablesInPrompt(
            config.frame.find(f => f.key === frame.value).prompt + ' ' + config.styles.find(f => f.key === style.value).prompt,
            config.colors.find(f => f.key === color.value).name,
            config.tableTexture.find(f => f.key === tabletop.value).prompt,
        ),
    );

    // Update or add the new generation result
    const existingIndex = generationResults.value.findIndex(r => r.productId === productId);
    if (existingIndex !== -1) {
      generationResults.value[existingIndex] = result[0];
    } else {
      generationResults.value.push(result[0]);
    }

    // Add to pending generations
    pendingGenerations.value.add(result[0].generationId);

    // Start status checking if needed
    startStatusCheckingIfNeeded();

  } catch (error) {
    console.error('Error regenerating image:', error);
  }
}

const startStatusCheckingIfNeeded = () => {
  // Only start if not already running and there are pending generations
  if (!statusCheckInterval.value && pendingGenerations.value.size > 0) {
    statusCheckInterval.value = setInterval(checkPendingGenerations, 3000);
    // Also run immediately
    checkPendingGenerations();
  }
}

// Cleanup interval on component unmount
onUnmounted(() => {
  if (statusCheckInterval.value) {
    clearInterval(statusCheckInterval.value);
    statusCheckInterval.value = null;
  }
})
</script>

